import React, { useState, useCallback, useRef } from 'react';
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  ConnectionMode,
  Panel,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { FiPlus, FiSave, FiTrash2, FiEdit3, FiDownload } from 'react-icons/fi';

// CustomNode component'ini önce tanımlayalım
function CustomNode({ data, selected, id }) {
  const onDelete = () => {
    // Parent component'e silme işlemini bildir
    if (data.onDelete) {
      data.onDelete(id);
    }
  };

  return (
    <div className={`px-4 py-2 shadow-md rounded-md border-2 bg-white dark:bg-dark-200 ${
      data.isMain 
        ? 'border-red-500 dark:border-red-400' 
        : selected 
          ? 'border-blue-500 dark:border-blue-400' 
          : 'border-gray-300 dark:border-gray-600'
    }`}>
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {data.label}
        </div>
        {!data.isMain && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="ml-2 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
          >
            <FiTrash2 size={12} />
          </button>
        )}
      </div>
    </div>
  );
}

const nodeTypes = {
  custom: CustomNode,
};

const initialNodes = [
  {
    id: '1',
    type: 'custom',
    position: { x: 250, y: 25 },
    data: { label: 'Ana Konu', isMain: true },
  },
];

const initialEdges = [];

const MindMapEditor = ({ onSave, initialData = null }) => {
  const [nodes, setNodes, onNodesChange] = useNodesState(initialData?.nodes || initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialData?.edges || initialEdges);
  const [selectedNode, setSelectedNode] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState('');
  const reactFlowWrapper = useRef(null);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);

  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge(params, eds)),
    [setEdges]
  );

  const onNodeClick = useCallback((event, node) => {
    setSelectedNode(node);
    setEditText(node.data.label);
    setIsEditing(true);
  }, []);

  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
    setIsEditing(false);
  }, []);

  const deleteNode = useCallback((nodeId) => {
    setNodes((nds) => nds.filter((node) => node.id !== nodeId));
    setEdges((eds) => eds.filter((edge) => edge.source !== nodeId && edge.target !== nodeId));
  }, [setNodes, setEdges]);

  const addNode = useCallback(() => {
    const nodeId = `${Date.now()}`;
    const newNode = {
      id: nodeId,
      type: 'custom',
      position: { 
        x: Math.random() * 400 + 100, 
        y: Math.random() * 300 + 100 
      },
      data: { 
        label: 'Yeni Node',
        onDelete: deleteNode
      },
    };
    setNodes((nds) => [...nds, newNode]);
  }, [setNodes, deleteNode]);

  const updateNodeLabel = useCallback(() => {
    if (selectedNode && editText.trim()) {
      setNodes((nds) =>
        nds.map((node) =>
          node.id === selectedNode.id
            ? { ...node, data: { ...node.data, label: editText.trim() } }
            : node
        )
      );
      setIsEditing(false);
      setSelectedNode(null);
    }
  }, [selectedNode, editText, setNodes]);

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();

      const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
      const position = reactFlowInstance.project({
        x: event.clientX - reactFlowBounds.left,
        y: event.clientY - reactFlowBounds.top,
      });

      const nodeId = `${Date.now()}`;
      const newNode = {
        id: nodeId,
        type: 'custom',
        position,
        data: { 
          label: 'Yeni Node',
          onDelete: deleteNode
        },
      };

      setNodes((nds) => [...nds, newNode]);
    },
    [reactFlowInstance, setNodes, deleteNode]
  );

  const exportData = useCallback(() => {
    const data = {
      nodes: nodes.map(node => ({
        id: node.id,
        position: node.position,
        data: {
          label: node.data.label,
          isMain: node.data.isMain || false
        }
      })),
      edges: edges.map(edge => ({
        id: edge.id,
        source: edge.source,
        target: edge.target
      })),
      viewport: reactFlowInstance?.getViewport()
    };
    
    if (onSave) {
      onSave(data);
    }
    
    return data;
  }, [nodes, edges, reactFlowInstance, onSave]);

  const clearAll = useCallback(() => {
    setNodes(initialNodes);
    setEdges(initialEdges);
  }, [setNodes, setEdges]);

  return (
    <div className="w-full h-full">
      <div className="h-12 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between px-4">
        <div className="flex items-center space-x-2">
          <button
            onClick={addNode}
            className="flex items-center px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white text-sm rounded-md transition-colors"
          >
            <FiPlus className="mr-1" size={14} />
            Node Ekle
          </button>
          <button
            onClick={exportData}
            className="flex items-center px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white text-sm rounded-md transition-colors"
          >
            <FiSave className="mr-1" size={14} />
            Kaydet
          </button>
          <button
            onClick={clearAll}
            className="flex items-center px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white text-sm rounded-md transition-colors"
          >
            <FiTrash2 className="mr-1" size={14} />
            Temizle
          </button>
        </div>
        
        <div className="text-sm text-gray-600 dark:text-gray-400">
          {nodes.length} node, {edges.length} bağlantı
        </div>
      </div>

      <div className="h-[calc(100%-3rem)]" ref={reactFlowWrapper}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onInit={setReactFlowInstance}
          onNodeClick={onNodeClick}
          onPaneClick={onPaneClick}
          onDrop={onDrop}
          onDragOver={onDragOver}
          nodeTypes={nodeTypes}
          connectionMode={ConnectionMode.Loose}
          fitView
          defaultEdgeOptions={{
            style: { strokeWidth: 2, stroke: '#3b82f6' },
            type: 'smoothstep',
          }}
          connectionLineStyle={{ strokeWidth: 2, stroke: '#3b82f6' }}
          snapToGrid={true}
          snapGrid={[15, 15]}
        >
          <Controls />
          <MiniMap />
          <Background variant="dots" gap={12} size={1} />
        </ReactFlow>
      </div>

      {/* Düzenleme Modal'ı */}
      {isEditing && selectedNode && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-dark-200 rounded-lg p-6 w-96">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Node'u Düzenle
            </h3>
            <input
              type="text"
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-dark-300 dark:text-white"
              placeholder="Node metni..."
              autoFocus
            />
            <div className="flex justify-end space-x-2 mt-4">
              <button
                onClick={() => {
                  setIsEditing(false);
                  setSelectedNode(null);
                }}
                className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
              >
                İptal
              </button>
              <button
                onClick={updateNodeLabel}
                className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md"
              >
                Kaydet
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MindMapEditor;
